import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  // useCallback,
} from 'react';
import { childToParentMatrix, Coord, SVGtoDOMMatrix } from '../../types/LayoutTypes';
import { snap as snapAxis } from '@/common';

interface TransformGroupProps {
  id: string;
  mode: 'translate' | 'rotate';
  snap?: { interval: number, offset: number };
  onFrame?: (id: string, value: Coord | number) => void;
  onUse?: (id: string, value: Coord | number, ctm: DOMMatrixReadOnly) => void;
  children: React.ReactNode;
}
const TransformGroup = ({
  id,
  mode,
  snap,
  onFrame,
  onUse,
  children,
}: TransformGroupProps) : JSX.Element => {

  const groupElement = useRef<SVGGElement>(null);
  const parentElement = useRef<SVGGraphicsElement>();
  const isDragging = useRef(false);
  const clickOffset = useRef<Coord>({ x: 0, y: 0 });
  const prevAngle = useRef(0);
  const translateRef = useRef<Coord>({ x: 0, y: 0 });
  const [ translate, setTranslate ] = useState<Coord>({ x: 0, y: 0 });
  const rotateRef = useRef(0);
  const [ rotate, setRotate ] = useState(0);

  
  useEffect(() => {
    parentElement.current = groupElement.current!.parentElement as unknown as SVGGraphicsElement;
  }, []);

  useEffect(() => {
    if (snap && mode == 'translate') {
      translateRef.current.y = snapAxis(translateRef.current.y, snap.interval, snap.offset);
      setTranslate(translateRef.current);
    }
  }, [snap, mode, setTranslate]);

  const handleHover = useCallback((e) => {
      groupElement.current!.style.cursor = 'move';
      e.stopPropagation();
  }, []);

  const handleUnHover = useCallback((e) => {
      groupElement.current!.style.removeProperty('cursor');
      e.stopPropagation();
  }, []);

  const handleDragBegin = useCallback((e) => {
      isDragging.current = true;
      // group transform will use coords in its parent space
      const worldToParentMatrix = SVGtoDOMMatrix(parentElement.current!.getCTM()!).inverse();
      if (mode == 'translate') {
        // store worldspace offset of click from group origin
        const op = SVGtoDOMMatrix(groupElement.current!.getCTM()!).transformPoint(new DOMPoint());
        clickOffset.current = { x: e.nativeEvent.offsetX - op.x, y: e.nativeEvent.offsetY - op.y };
      }
      else if (mode == 'rotate') {
        const cp = worldToParentMatrix.transformPoint(new DOMPoint(e.nativeEvent.offsetX, e.nativeEvent.offsetY));
        prevAngle.current = Math.atan2(cp.y, cp.x) * 180 / Math.PI;
      }
      window.addEventListener('mouseup', handleDragEnd);
      e.stopPropagation();
  }, [mode]);

  const handleDragEnd = useCallback((e) => {
      isDragging.current = false;
      window.removeEventListener('mouseup', handleDragEnd);
      const cpm = childToParentMatrix(groupElement.current!, parentElement.current!);
      onUse?.(id, mode == 'translate' ? translateRef.current : rotateRef.current, cpm);
      e.stopPropagation();
  }, [id, mode, onUse]);

  const handleDrag = useCallback((e) => {
      if (!isDragging.current) return;
      const worldToParentMatrix = SVGtoDOMMatrix(parentElement.current!.getCTM()!).inverse();
      if (mode == 'translate') {
        // move group to pointer minus the click offset
        const wp = new DOMPoint(e.nativeEvent.offsetX - clickOffset.current.x, e.nativeEvent.offsetY - clickOffset.current.y);
        const p = worldToParentMatrix.transformPoint(wp);
        if (snap)
          p.y = snapAxis(p.y, snap.interval, snap.offset);
        translateRef.current = { x: p.x, y: p.y };
        onFrame?.(id, translateRef.current);
        setTranslate(translateRef.current);
      }
      else if (mode == 'rotate') {
        const rp = worldToParentMatrix.transformPoint(new DOMPoint(e.nativeEvent.offsetX, e.nativeEvent.offsetY));
        const dragAngle = Math.atan2(rp.y, rp.x) * 180 / Math.PI;
        rotateRef.current = (rotateRef.current + dragAngle - prevAngle.current) % 360;
        prevAngle.current = dragAngle;
        onFrame?.(id, rotateRef.current);
        setRotate(rotateRef.current);
      }
      e.stopPropagation();
  }, [id, mode, snap, onFrame]);

  return (
    <g
      ref={groupElement}
      transform={mode == 'translate' ? `translate(${translate.x}, ${translate.y})`: `rotate(${rotate})`}
      style={{ pointerEvents: 'auto' }}
      onMouseEnter={handleHover}
      onMouseLeave={handleUnHover}
      onMouseDown={handleDragBegin}
      onMouseUp={handleDragEnd}
      onMouseMove={handleDrag}>
      {/* <rect ref={invisBox} fill='transparent' /> */}
      {children}
    </g>
  );
};

export default TransformGroup;
